import { useState } from 'react';
import * as React from 'react';
import styled from 'styled-components';
import Row from '@sdk/components/ui/spacing/Row';
import Text18 from '@sdk/components/ui/typography/Text18';
import Text14 from '@sdk/components/ui/typography/Text14';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import gettext, { ngettext } from '@sdk/lang/gettext';
import { ClientFlagsProvider, useClientFlags } from '@sdk/session/ClientFlagsContext';
import isOrgFeatureEnabled from '@sdk/authentication/helpers/isOrgFeatureEnabled';
import type { AuthUserType } from '@sdk/authentication/types/AuthUserType';
import { Feature } from '@sdk/common/features';

import getRequiredSubscriptionNameForFeature from '@web/account/utils/getRequiredSubscriptionNameForFeature';
import userDocumentsImage from '@web/assets/images/premium-features/user-documents.png';
import shiftFeedbackImage from '@web/assets/images/premium-features/shift-feedback.png';

// @TODO: move those to react folder
import timeclockBannerImage from '../../src/assets/images/premium-features/timeclock-banner-new.png';
import reportsBannerImage from '../../src/assets/images/premium-features/labor-report-banner-new.png';
import shiftFeedbackBannerImage from '../../src/assets/images/premium-features/shift-feedback-banner-new.png';
import shiftTasksBannerImage from '../../src/assets/images/premium-features/shift-tasks-banner-new.png';
import messagesImage from '../../src/assets/images/premium-features/messages-banner-new.png';
import unavailabilityImage from '../../src/assets/images/premium-features/unavailability-banner-new.png';
import tagsImage from '../../src/assets/images/premium-features/tags-banner-new.png';
import laborCostImage from '../../src/assets/images/premium-features/labor-cost-banner-new.png';
import timeoffImage from '../../src/assets/images/premium-features/timeoff-banner-new.png';
import closeSvg from '../../src/assets/svg/UI/close.svg';
import crownSvg from '../../src/assets/svg/UI/crown.svg';

type ContainerProps = {
  standalone: boolean;
  clickable: boolean;
};

const isFeatureBannerVisible = (feature, user) =>
  user.org.isOnTrial &&
  isOrgFeatureEnabled(feature, user) &&
  !user.settings.clientFlags[`premiumFeatureBanner-${feature}-dismissed`];

const getGradientedBackground = (color: string): string => `
linear-gradient(47.18deg, rgba(0, 0, 0, 0.012) 63.26%, rgba(0, 0, 0, 0.052) 95.14%),
linear-gradient(270deg, ${color} 38.25%, #fafbfc 65.66%);
`;

const Container = styled.div<ContainerProps>`
  background: ${({ standalone, theme }): string =>
    standalone ? 'white' : getGradientedBackground(theme.colors.bodyBg)};
  position: relative;
  padding: 15px;
  border-radius: ${({ standalone }): string => (standalone ? '8px' : '0')};
  cursor: ${({ clickable }): string => (clickable ? 'pointer' : 'default')};
`;

const CloseBtn = styled('img')`
  position: absolute;
  right: 0;
  top: 0;
  padding: 5px;
  cursor: pointer;
`;

const BannerImg = styled('img')`
  width: 240px;
  height: 135px;
  margin-right: 30px;
`;

const CrownImg = styled('img')`
  position: relative;
  top: -2px;
  margin-right: 5px;
`;

const CenterVert = styled.div`
  display: flex;
  align-items: center;
  flex-flow: row;
  max-width: 800px;
`;

const BANNER_IMAGES = {
  clockIn: timeclockBannerImage,
  reports: reportsBannerImage,
  shiftFeedback: shiftFeedbackBannerImage,
  shiftTasks: shiftTasksBannerImage,
  messages: messagesImage,
  groupMessages: messagesImage,
  unavailability: unavailabilityImage,
  tags: tagsImage,
  laborCost: laborCostImage,
  leaveTypes: timeoffImage,
  userDocuments: userDocumentsImage,
};

const TITLES = {
  clockIn: gettext('Simplify your time tracking and payroll'),
  reports: gettext('Stay on top of your data'),
  shiftFeedback: gettext('Get feedback from your employees'),
  shiftTasks: gettext('Attach tasks to shifts and improve efficiency'),
  messages: gettext('Communicate more efficiently from a single platform'),
  groupMessages: gettext('Communicate more efficiently from a single platform'),
  unavailability: gettext('Keep track of unavailability'),
  tags: gettext('Add specifics to your shifts'),
  laborCost: gettext('Optimize your spending as you schedule'),
  leaveTypes: gettext('Make your leave management easier'),
  userDocuments: gettext('Keep track of employee documents'),
};

const DESCRIPTIONS = {
  clockIn: gettext(
    'Easily track employee hours, and export payroll reports for seamless payroll processing.'
  ),
  reports: gettext(
    'Understand your data by pulling reports for a day, week or month, or for customizable periods. Make informed adjustments for the future and drive your business forward.'
  ),
  shiftFeedback: gettext(
    'Gain insight into shift satisfaction to support retention of your employees.'
  ),
  shiftTasks: gettext(
    'Clarify your team’s responsibilities during each shift to ensure employees are aligned on their to-dos anytime.'
  ),
  messages: gettext(
    'Send messages to groups or individuals, keep everyone informed and build a better company culture. Easily reach employees within groups, locations or specific positions and get notified of their responses in real time.'
  ),
  groupMessages: gettext(
    'Send messages to groups or individuals, keep everyone informed and build a better company culture. Easily reach employees within groups, locations or specific positions and get notified of their responses in real time.'
  ),
  unavailability: gettext(
    'Set your team’s unavailability and approve unavailability changes to avoid scheduling employees when they can’t work and ensure proper staffing.'
  ),
  tags: gettext(
    'Create tags to add further details to shifts and reports, ensure all jobs are covered, and keep employees clear on what’s expected of them during each shift.'
  ),
  laborCost: gettext(
    'Add wages to estimate and optimize your spending as you schedule. Upload projected sales to monitor your labor percentage, and avoid exceeding labor budget or scheduling overtime.'
  ),
  leaveTypes: gettext(
    'Allow employees to request paid time off for leave types your business covers. Set PTO caps per leave type or in aggregate, and get alerted when they are exceeded. '
  ),
  userDocuments: gettext(
    'Upload employee licenses and certifications, get reminded of renewal dates, and keep everything in one place.'
  ),
};

type Props = {
  feature: Feature;
  // TODO this prop would be replaced with useSession() hook eventually
  // when the higher-level components would provide the required context
  currentUser: AuthUserType;
  openFeatureModal: () => void;
  linkAction?: () => void;
  linkText?: number;
  linkEnabled?: boolean;
  standalone?: boolean;
  featureAltTexts?: string;
  closable?: boolean;
};

const PremiumFeatureBannerInner = ({
  feature,
  currentUser,
  openFeatureModal,
  linkAction,
  linkText,
  linkEnabled,
  standalone,
  featureAltTexts,
  closable = true,
}: Props) => {
  const { trialDays } = currentUser.org;
  const [isVisible, setVisible] = useState(isFeatureBannerVisible(feature, currentUser));
  const [isEnabled] = useState(isOrgFeatureEnabled(feature, currentUser));
  const { updateClientFlags } = useClientFlags();

  const onCloseClicked = (e: React.MouseEvent): void => {
    e.stopPropagation();
    updateClientFlags({ [`premiumFeatureBanner-${feature}-dismissed`]: true });
    setVisible(false);
  };

  const onLinkClicked = (e: React.MouseEvent): void => {
    e.stopPropagation();
    linkAction();
  };

  const onContainerClick = (): void => {
    if (openFeatureModal) {
      openFeatureModal();
    }
  };

  if (!isVisible) {
    return null;
  }

  const requiredSubscriptionName = getRequiredSubscriptionNameForFeature(feature, currentUser);
  let availabilityInfo = null;

  if (!isEnabled) {
    availabilityInfo = gettext('Available in %(requiredSubscriptionName)s', {
      requiredSubscriptionName,
    });
  } else if (trialDays) {
    availabilityInfo = ngettext(
      'Free for %(trialDays)s day; afterwards, available in %(requiredSubscriptionName)s',
      'Free for %(trialDays)s days; afterwards, available in %(requiredSubscriptionName)s',
      trialDays,
      { trialDays, requiredSubscriptionName }
    );
  }

  return (
    <Container onClick={onContainerClick} standalone={standalone} clickable={!!openFeatureModal}>
      {closable && <CloseBtn src={closeSvg} onClick={onCloseClicked} />}
      <Row>
        <BannerImg src={BANNER_IMAGES[feature]} />
        <CenterVert>
          <div>
            <Row mBottom={10}>
              <Text18 themeColor={ThemeColorType.primaryText} semiBold>
                {TITLES[featureAltTexts || feature]}
              </Text18>
            </Row>
            {availabilityInfo && (
              <Row mBottom={10}>
                <Text14 semiBold>
                  <CrownImg src={crownSvg} />
                  {availabilityInfo}
                </Text14>
              </Row>
            )}
            <Row>
              <Text14>{DESCRIPTIONS[featureAltTexts || feature]}</Text14>
            </Row>
            {linkEnabled && (
              <Row mTop={5}>
                <a onClick={onLinkClicked}>{linkText} &gt;</a>
              </Row>
            )}
          </div>
        </CenterVert>
      </Row>
    </Container>
  );
};

export const PremiumFeatureBanner = (props: Props) => (
  <ClientFlagsProvider>
    <PremiumFeatureBannerInner {...props} />
  </ClientFlagsProvider>
);

export default {
  ReactComponent: PremiumFeatureBanner,
  bindings: {
    feature: '@',
    currentUser: '<',
    openFeatureModal: '&?',
    linkAction: '&?',
    linkText: '@?',
    linkEnabled: '<?',
    standalone: '<?',
    featureAltTexts: '@?',
    closable: '<?',
  },
};
